import {initWizard} from '../Wizard/Wizard';

const revealGateError = gateError => {
    gateError?.removeAttribute('hidden');
    gateError?.scrollIntoView({block: 'nearest', behavior: 'smooth'});
};

const isOrderMatchMapped = (panel, matchFieldIds) => {
    const gateError      = panel.querySelector('[data-mapping-gate-error]');
    const mappingSelects = [...panel.querySelectorAll('.column-mapping-cell select')];
    const hasIdColumn    = mappingSelects.some(select => matchFieldIds.includes(select.value));

    if (hasIdColumn) {
        gateError?.setAttribute('hidden', '');

        return true;
    }

    revealGateError(gateError);

    return false;
};

const findRepeatedSlottedColumn = (panel, slottedFieldIds) => {
    const filledParts = new Set();

    for (const cell of panel.querySelectorAll('.column-mapping-cell')) {
        const fieldId = cell.querySelector(':scope > od-select select')?.value ?? '';

        if (!slottedFieldIds.includes(fieldId)) {
            continue;
        }

        const part = `${fieldId}|${cell.querySelector('[data-target="field-slot"] select').value}`;

        if (filledParts.has(part)) {
            const columnNumber = Number.parseInt(cell.dataset.column, 10) + 1;

            return columnNumber;
        }

        filledParts.add(part);
    }

    return null;
};

const isEachSlottedPartMappedOnce = (panel, slottedFieldIds) => {
    const gateError    = panel.querySelector('[data-slot-gate-error]');
    const columnNumber = findRepeatedSlottedColumn(panel, slottedFieldIds);

    if (columnNumber === null) {
        gateError?.setAttribute('hidden', '');

        return true;
    }

    gateError.querySelector('p').textContent = gateError.dataset.messageTemplate.replace('%d', String(columnNumber));

    revealGateError(gateError);

    return false;
};

const isAcceptedNumber = (value, maximum) => {
    if (!/^\d+$/.test(value)) {
        return false;
    }

    const number = BigInt(value);

    return number > BigInt(0) && number <= BigInt(maximum);
};

const isAcceptedArgument = (value, parameter) => {
    if (parameter.isNumber) {
        return isAcceptedNumber(value, parameter.maximum);
    }

    return value !== '' && [...value].length <= parameter.maximumLength;
};

const findUnacceptedArgument = (panel, parameterizedFields) => {
    for (const cell of panel.querySelectorAll('.column-mapping-cell')) {
        const fieldId    = cell.querySelector(':scope > od-select select')?.value ?? '';
        const parameters = parameterizedFields[fieldId] ?? [];

        for (const [position, parameter] of parameters.entries()) {
            const argument = cell.querySelector(`input[data-target="argument"][data-position="${position}"]`);

            if (!isAcceptedArgument(argument.value.trim(), parameter)) {
                const columnNumber = Number.parseInt(cell.dataset.column, 10) + 1;

                return {columnNumber, placeholder: parameter.placeholder};
            }
        }
    }

    return null;
};

const isEachArgumentAccepted = (panel, parameterizedFields) => {
    const gateError  = panel.querySelector('[data-argument-gate-error]');
    const unaccepted = findUnacceptedArgument(panel, parameterizedFields);

    if (unaccepted === null) {
        gateError?.setAttribute('hidden', '');

        return true;
    }

    gateError.querySelector('p').textContent = gateError.dataset.messageTemplate
        .replace('%1$d', String(unaccepted.columnNumber))
        .replace('%2$s', unaccepted.placeholder);

    revealGateError(gateError);

    return false;
};

const readArgumentSlot = (cell, parameters) => JSON.stringify(parameters.map((parameter, position) => {
    const argument = cell.querySelector(`input[data-target="argument"][data-position="${position}"]`).value.trim();

    return parameter.isNumber ? String(Number.parseInt(argument, 10)) : argument.toLowerCase();
}));

const findRepeatedArgumentColumn = (panel, parameterizedFields) => {
    const filledSlots = new Set();

    for (const cell of panel.querySelectorAll('.column-mapping-cell')) {
        const fieldId    = cell.querySelector(':scope > od-select select')?.value ?? '';
        const parameters = parameterizedFields[fieldId];

        if (!parameters) {
            continue;
        }

        const slot = `${fieldId}|${readArgumentSlot(cell, parameters)}`;

        if (filledSlots.has(slot)) {
            const columnNumber = Number.parseInt(cell.dataset.column, 10) + 1;

            return columnNumber;
        }

        filledSlots.add(slot);
    }

    return null;
};

const isEachArgumentSlotMappedOnce = (panel, parameterizedFields) => {
    const gateError    = panel.querySelector('[data-argument-repeat-gate-error]');
    const columnNumber = findRepeatedArgumentColumn(panel, parameterizedFields);

    if (columnNumber === null) {
        gateError?.setAttribute('hidden', '');

        return true;
    }

    gateError.querySelector('p').textContent = gateError.dataset.messageTemplate.replace('%d', String(columnNumber));

    revealGateError(gateError);

    return false;
};

const findRepeatedWarehouseColumn = (panel, warehouseFieldIds) => {
    const loadedWarehouses = new Set();

    for (const cell of panel.querySelectorAll('.column-mapping-cell')) {
        const fieldId = cell.querySelector(':scope > od-select select')?.value ?? '';

        if (!warehouseFieldIds.includes(fieldId)) {
            continue;
        }

        const warehouse = cell.querySelector('[data-target="field-warehouse"] select').value;

        if (loadedWarehouses.has(warehouse)) {
            const columnNumber = Number.parseInt(cell.dataset.column, 10) + 1;

            return columnNumber;
        }

        loadedWarehouses.add(warehouse);
    }

    return null;
};

const isEachWarehouseLoadedOnce = (panel, warehouseFieldIds) => {
    const gateError    = panel.querySelector('[data-warehouse-gate-error]');
    const columnNumber = findRepeatedWarehouseColumn(panel, warehouseFieldIds);

    if (columnNumber === null) {
        gateError?.setAttribute('hidden', '');

        return true;
    }

    gateError.querySelector('p').textContent = gateError.dataset.messageTemplate.replace('%d', String(columnNumber));

    revealGateError(gateError);

    return false;
};

export function initImportWizard(wizard) {
    const importForm          = wizard.querySelector('form');
    const matchFieldIds       = (importForm?.dataset.matchFieldIds ?? '').split(',').filter(Boolean);
    const slottedFieldIds     = Object.keys(JSON.parse(importForm?.dataset.slotLabels ?? '{}'));
    const parameterizedFields = JSON.parse(importForm?.dataset.parameterizedFields ?? '{}');
    const warehouseFieldIds   = (importForm?.dataset.warehouseFieldIds ?? '').split(',').filter(Boolean);

    initWizard(wizard, {
        validate(panel) {
            if (panel.dataset.wizardStep !== 'mapping') {
                return true;
            }

            return isOrderMatchMapped(panel, matchFieldIds)
                && isEachSlottedPartMappedOnce(panel, slottedFieldIds)
                && isEachArgumentAccepted(panel, parameterizedFields)
                && isEachArgumentSlotMappedOnce(panel, parameterizedFields)
                && isEachWarehouseLoadedOnce(panel, warehouseFieldIds);
        },

        onStepEnter(panel) {
            if (panel.dataset.wizardStep === 'confirm') {
                panel.querySelector('input[name="templateName"]')?.focus();
            }
        },
    });
}
